import { EDITABLE_REPORT_TARGET_KEYS, REPORT_GOAL_MAX_CENTS, type EditableReportTargetsView, type ReportGoalsResponse, type SaveReportGoalsBody } from "@gioia365/contracts";
import type { Locale, MessageKey } from "@gioia365/i18n";
import { formatEuroCents } from "../../i18n/format";
import type { Translate } from "../../i18n/locale";
import { targetNumber } from "../../reports/report-format";

// La bozza di Impostazioni → «Obiettivi» (piano M7, Task 6; IMP-2; sul modello di `assignment-draft.ts`): le modifiche sono i soli campi
// toccati sopra gli obiettivi salvati, come li scrive la persona (testo), così una rilettura dopo un conflitto (`409 version_conflict`)
// rimette le modifiche sopra gli obiettivi nuovi e i campi non toccati prendono quelli. Gli importi si scrivono in euro («3200»,
// «3200,50») e si salvano in centesimi; le percentuali con al più un decimale, «Da» non più alto di «A», come il server. Funzioni pure:
// i testi dei campi, il problema che impedisce di salvare, ciò che cambia detto a parole e il corpo della richiesta.

export type TargetKey = keyof EditableReportTargetsView;

/** I testi dei campi di una percentuale. */
export type TargetText = { min: string; max: string };

/** I campi toccati, sopra gli obiettivi salvati: il valore medio, le percentuali e gli obiettivi delle persone (per appartenenza). */
export type GoalsEdits = { averageSale?: string; targets?: Partial<Record<TargetKey, TargetText>>; goals?: Record<string, string> };

/** I testi di tutti i campi della pagina: quelli salvati con sopra le modifiche. */
export type GoalsDraft = { averageSale: string; targets: Record<TargetKey, TargetText>; goals: Record<string, string> };

/** Il separatore dei decimali della lingua, per scrivere gli importi e le percentuali come li legge la persona. */
const decimalOf = (locale: Locale) => (locale === "it" ? "," : ".");

/** Un importo in centesimi come si scrive nel campo: «3200», «3200,50»; vuoto senza importo. */
export function euroTextOf(locale: Locale, cents: number | null): string {
  if (cents === null) return "";
  const euros = Math.floor(cents / 100);
  const rest = cents % 100;
  return rest === 0 ? String(euros) : `${euros}${decimalOf(locale)}${String(rest).padStart(2, "0")}`;
}

/** Una percentuale come si scrive nel campo: «18», «18,5»; vuoto senza numero. */
export function percentTextOf(locale: Locale, value: number | undefined): string {
  if (value === undefined) return "";
  const tenths = Math.round(value * 10);
  return tenths % 10 === 0 ? String(tenths / 10) : `${Math.floor(tenths / 10)}${decimalOf(locale)}${tenths % 10}`;
}

const EURO = /^(\d{1,8})(?:[.,](\d{1,2}))?$/;
const PERCENT = /^(\d{1,3})(?:[.,](\d))?$/;

/**
 * Un importo scritto in euro in centesimi: vuoto è `null` (nessun importo), una forma sbagliata o fuori misura `undefined`. Niente
 * separatori delle migliaia: «3.200» si rifiuta, «3.20» è 3 euro e 20, come nel campo di «Venduto».
 */
export function centsOfText(text: string): number | null | undefined {
  const trimmed = text.trim();
  if (trimmed === "") return null;
  const match = EURO.exec(trimmed);
  if (!match) return undefined;
  const cents = Number(match[1]) * 100 + Number((match[2] ?? "").padEnd(2, "0"));
  return cents <= REPORT_GOAL_MAX_CENTS ? cents : undefined;
}

/** Una percentuale scritta: vuoto è `null`, una forma sbagliata o fuori da più di 0–100 `undefined`. */
export function percentOfText(text: string): number | null | undefined {
  const trimmed = text.trim();
  if (trimmed === "") return null;
  const match = PERCENT.exec(trimmed);
  if (!match) return undefined;
  const tenths = Number(match[1]) * 10 + Number(match[2] ?? "0");
  return tenths > 0 && tenths <= 1000 ? tenths / 10 : undefined;
}

/** I testi salvati di tutti i campi. */
export function savedTextOf(base: ReportGoalsResponse, locale: Locale): GoalsDraft {
  const targets = Object.fromEntries(
    EDITABLE_REPORT_TARGET_KEYS.map((key) => [key, { min: percentTextOf(locale, base.targets[key].min), max: percentTextOf(locale, base.targets[key].max) }]),
  ) as Record<TargetKey, TargetText>;
  const goals = Object.fromEntries(base.people.map((row) => [row.person.membershipId, euroTextOf(locale, row.salesGoalCents)]));
  return { averageSale: euroTextOf(locale, base.averageSaleCents), targets, goals };
}

/** I campi che la pagina mostra: quelli salvati con sopra le modifiche. Le persone che non sono più fra le righe non contano. */
export function draftOf(saved: GoalsDraft, edits: GoalsEdits): GoalsDraft {
  const goals = { ...saved.goals };
  for (const [membershipId, text] of Object.entries(edits.goals ?? {})) if (membershipId in goals) goals[membershipId] = text;
  return { averageSale: edits.averageSale ?? saved.averageSale, targets: { ...saved.targets, ...edits.targets }, goals };
}

export type GoalsProblem =
  | { kind: "averageSale" }
  | { kind: "goal"; membershipId: string }
  | { kind: "percent"; key: TargetKey; field: "min" | "max" }
  | { kind: "minMissing"; key: TargetKey }
  | { kind: "minOverMax"; key: TargetKey };

/** I problemi che impediscono di salvare, nell'ordine della pagina, gli stessi controlli del server. */
export function goalsProblems(draft: GoalsDraft, people: ReportGoalsResponse["people"]): GoalsProblem[] {
  const problems: GoalsProblem[] = [];
  const average = centsOfText(draft.averageSale);
  if (average === undefined || average === 0) problems.push({ kind: "averageSale" });
  for (const row of people) {
    if (centsOfText(draft.goals[row.person.membershipId] ?? "") === undefined) problems.push({ kind: "goal", membershipId: row.person.membershipId });
  }
  for (const key of EDITABLE_REPORT_TARGET_KEYS) {
    const min = percentOfText(draft.targets[key].min);
    const max = percentOfText(draft.targets[key].max);
    if (min === undefined) problems.push({ kind: "percent", key, field: "min" });
    else if (min === null) problems.push({ kind: "minMissing", key });
    if (max === undefined) problems.push({ kind: "percent", key, field: "max" });
    if (typeof min === "number" && typeof max === "number" && min > max) problems.push({ kind: "minOverMax", key });
  }
  return problems;
}

/** I nomi delle percentuali: le stesse parole dei numeri del Report. */
export const TARGET_NAMES: Readonly<Record<TargetKey | "discoOnCalls" | "demoShow", MessageKey>> = {
  discoOnCalls: "reports.kpis.discoOnCalls.label",
  discoOnAnswered: "reports.kpis.discoOnAnswered.label",
  discoShow: "reports.kpis.discoShow.label",
  demoOnDisco: "reports.kpis.demoOnDisco.label",
  demoShow: "reports.kpis.demoShow.label",
  closeRate: "reports.kpis.closeRate.label",
};

/** Il problema a parole, per la barra della bozza e sotto il campo. */
export function problemText(t: Translate, problem: GoalsProblem, nameOf: (membershipId: string) => string): string {
  switch (problem.kind) {
    case "averageSale":
      return t("goalsSettings.problems.averageSale");
    case "goal":
      return t("goalsSettings.problems.goal", { name: nameOf(problem.membershipId) });
    case "percent":
      return t("goalsSettings.problems.percent", { name: t(TARGET_NAMES[problem.key]) });
    case "minMissing":
      return t("goalsSettings.problems.minMissing", { name: t(TARGET_NAMES[problem.key]) });
    case "minOverMax":
      return t("goalsSettings.problems.minOverMax", { name: t(TARGET_NAMES[problem.key]) });
  }
}

/** Una percentuale a parole: «18–24%», «20%». */
function targetWords(locale: Locale, min: number, max: number | null): string {
  return max === null ? `${targetNumber(locale, min)}%` : `${targetNumber(locale, min)}–${targetNumber(locale, max)}%`;
}

/** Un importo a parole, o «Nessun obiettivo» senza importo. */
function amountWords(t: Translate, locale: Locale, cents: number | null): string {
  return cents === null ? t("goalsSettings.noGoal") : formatEuroCents(locale, cents);
}

type Parsed = { averageSaleCents: number | null; targets: EditableReportTargetsView; goals: Map<string, number | null> };

/** I valori della bozza; `null` se un campo ha un problema (allora non si salva). */
function parsedOf(draft: GoalsDraft, people: ReportGoalsResponse["people"]): Parsed | null {
  if (goalsProblems(draft, people).length > 0) return null;
  const targets = Object.fromEntries(
    EDITABLE_REPORT_TARGET_KEYS.map((key) => {
      const min = percentOfText(draft.targets[key].min) as number;
      const max = percentOfText(draft.targets[key].max);
      return [key, typeof max === "number" ? { min, max } : { min }];
    }),
  ) as EditableReportTargetsView;
  const goals = new Map(people.map((row) => [row.person.membershipId, centsOfText(draft.goals[row.person.membershipId] ?? "") as number | null]));
  return { averageSaleCents: centsOfText(draft.averageSale) as number | null, targets, goals };
}

/**
 * Ciò che cambia rispetto agli obiettivi salvati, a parole e nell'ordine della pagina: il valore medio, gli obiettivi delle persone nel
 * mese, poi le percentuali. Ogni frase è una modifica. Con un campo che ha un problema le frasi sono quelle dei campi giusti.
 */
export function describeGoalsChanges(base: ReportGoalsResponse, draft: GoalsDraft, monthName: string, t: Translate, locale: Locale): string[] {
  const sentences: string[] = [];
  const average = centsOfText(draft.averageSale);
  if (average !== undefined && average !== 0 && average !== base.averageSaleCents) {
    if (average === null) sentences.push(t("goalsSettings.changes.averageSaleRemoved", { before: amountWords(t, locale, base.averageSaleCents) }));
    else if (base.averageSaleCents === null) sentences.push(t("goalsSettings.changes.averageSaleSet", { after: formatEuroCents(locale, average) }));
    else sentences.push(t("goalsSettings.changes.averageSale", { before: formatEuroCents(locale, base.averageSaleCents), after: formatEuroCents(locale, average) }));
  }
  for (const row of base.people) {
    const after = centsOfText(draft.goals[row.person.membershipId] ?? "");
    if (after === undefined || after === row.salesGoalCents) continue;
    const name = row.person.displayName;
    if (after === null) sentences.push(t("goalsSettings.changes.goalRemoved", { name, month: monthName, before: amountWords(t, locale, row.salesGoalCents) }));
    else if (row.salesGoalCents === null) sentences.push(t("goalsSettings.changes.goalSet", { name, month: monthName, after: formatEuroCents(locale, after) }));
    else sentences.push(t("goalsSettings.changes.goal", { name, month: monthName, before: formatEuroCents(locale, row.salesGoalCents), after: formatEuroCents(locale, after) }));
  }
  for (const key of EDITABLE_REPORT_TARGET_KEYS) {
    const min = percentOfText(draft.targets[key].min);
    const max = percentOfText(draft.targets[key].max);
    if (typeof min !== "number" || max === undefined || (typeof max === "number" && min > max)) continue;
    const before = base.targets[key];
    if (min === before.min && (max ?? undefined) === before.max) continue;
    sentences.push(t("goalsSettings.changes.target", { name: t(TARGET_NAMES[key]), before: targetWords(locale, before.min, before.max ?? null), after: targetWords(locale, min, max) }));
  }
  return sentences;
}

/**
 * Il corpo di `PUT /api/settings/report-goals`: il mese, il valore medio, le quattro percentuali, gli obiettivi delle sole persone che
 * cambiano e la versione su cui la bozza è stata pensata; `null` se un campo ha un problema.
 */
export function saveBodyOf(base: ReportGoalsResponse, draft: GoalsDraft, expectedVersion: number): SaveReportGoalsBody | null {
  const parsed = parsedOf(draft, base.people);
  if (parsed === null) return null;
  const goals = base.people
    .filter((row) => parsed.goals.get(row.person.membershipId) !== row.salesGoalCents)
    .map((row) => ({ membershipId: row.person.membershipId, salesGoalCents: parsed.goals.get(row.person.membershipId) ?? null }));
  return { month: base.month, averageSaleCents: parsed.averageSaleCents, targets: parsed.targets, goals, expectedVersion };
}
